'use client'
import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import SectionHeader from '@/components/ui/SectionHeader'
import { Visual, VISUALS, VISUAL_CSS, loc } from './why/visuals'
import { useWhyTimeline } from './why/useWhyTimeline'

/**
 * Why MRN — the distributor case, told as a horizontal filmstrip.
 *
 * Vertical scroll drives a pinned slide through five full-screen chapters (the
 * horizontal motion is a deliberate, distinct moment on the single-page site).
 * Each chapter is a filled split — copy on one side, a clean diagram on the
 * other — so the frame never empties. Copy lives in i18n (t.why.chapters); the
 * visuals (./why/visuals) are mapped here by order; the scroll keyframe math
 * lives in ./why/useWhyTimeline.
 *
 * RTL: under fa/ar the page is dir=rtl, so the flex track lays the chapters out
 * right→left (chapter 1 at the right) and the copy/visual sides swap for free.
 * We only flip the slide direction (translate the track rightward instead of
 * left). The diagram SVGs stay LTR (forced) so their labels and the temperature
 * readout don't mirror — the same convention the existing cold-chain graphic
 * uses.
 *
 * prefers-reduced-motion → the same chapters stacked vertically, no scrub.
 */
export default function WhyJourney() {
  const { t, lang, isRTL } = useLanguage()
  const reduce = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)

  const chapters = t.why.chapters
  const N = chapters.length
  const temp = loc('4.2', lang) + '°C'

  const { trackT, drawFor, active, progress } = useWhyTimeline(ref, N, isRTL)

  // The filmstrip's fixed-height (100vh) panels gap on short chapters and clip
  // long ones on a phone, so below lg we fall back to the same calm stacked
  // layout reduced-motion uses — it scrolls normally and can't gap or clip.
  const [isNarrow, setIsNarrow] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)')
    const sync = () => setIsNarrow(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // ── stacked layout: reduced-motion OR narrow screens (mobile / tablet) ──
  if (reduce || isNarrow) {
    return (
      <section id="why" className="relative bg-surface py-24 sm:py-32">
        <div className="mx-auto max-w-5xl px-6">
          {/* TEMP (revert this week): visible mobile heading/sub block removed;
              keep an sr-only h2 for the outline (desktop filmstrip has one too). */}
          <h2 className="sr-only">{t.why.heading}</h2>
          <div className="flex flex-col gap-16">
            {chapters.map((p, i) => (
              <div
                key={p.headline}
                className="grid gap-8 sm:grid-cols-2 sm:items-center border-t border-line pt-10"
              >
                <div>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-accent">
                    {p.kicker}
                  </span>
                  <h3
                    className="mt-3 font-display-serif text-2xl sm:text-3xl leading-snug text-ink"
                    style={{ fontVariationSettings: '"opsz" 40, "wght" 600' }}
                  >
                    {p.headline}
                  </h3>
                  <p className="mt-4 text-sm sm:text-[15px] text-ink-muted leading-relaxed whitespace-pre-line">
                    {p.body}
                  </p>
                </div>
                <div className="flex justify-center">
                  <Visual kind={VISUALS[i]} draw={drawFor(VISUALS[i])} temp={temp} stacked />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* SVG sizing + fonts — without this the diagrams collapse to zero on
            iOS Safari (see VISUAL_CSS). The filmstrip branch has its own copy. */}
        <style>{VISUAL_CSS}</style>
      </section>
    )
  }

  // ── the horizontal filmstrip ──
  return (
    <section
      id="why"
      ref={ref}
      className="wj-root relative bg-surface"
      style={{ height: `${N * 92}vh` }}
    >
      {/* Real section heading for the document outline / SEO / screen readers.
          The filmstrip is otherwise all h3 chapters under no h2; this anchors
          the outline without disturbing the immersive frame (the visible h2 +
          sub render in the stacked path that mobile / reduced-motion get). */}
      <h2 className="sr-only">{t.why.heading}</h2>
      <div className="wj-stage sticky top-0 h-screen w-full overflow-hidden">
        <motion.div className="wj-track" style={{ transform: trackT, width: `${N * 100}vw` }}>
          {chapters.map((p, i) => (
            <article key={p.headline} className="wj-panel">
              <div className="wj-panel-text">
                <span className="wj-kicker">{p.kicker}</span>
                <h3 className="wj-headline">{p.headline}</h3>
                <p className="wj-body">{p.body}</p>
              </div>
              <div className="wj-panel-visual">
                <Visual kind={VISUALS[i]} draw={drawFor(VISUALS[i])} temp={temp} stacked={false} />
              </div>
            </article>
          ))}
        </motion.div>

        {/* persistent eyebrow */}
        <span className="wj-eyebrow">{t.why.section}</span>

        {/* Scroll progress — a single bar that fills as the visitor scrolls
            through the filmstrip, reaching full exactly as the last chapter
            lands. Decorative; the SR <p> below carries the position to
            assistive tech (skill: Progress Indicators, Dynamic Updates). */}
        <div className="wj-progress" aria-hidden>
          <motion.div className="wj-progress-fill" style={{ scaleX: progress }} />
        </div>
        <p className="sr-only" aria-live="polite">
          {`${loc(String(active + 1), lang)} / ${loc(String(N), lang)} — ${chapters[active].kicker}`}
        </p>
      </div>

      <style>{`
        .wj-stage {
          background:
            radial-gradient(120% 90% at 0% 0%, color-mix(in oklab, var(--color-blue-soft) 16%, var(--color-surface)) 0%, var(--color-surface) 55%);
        }
        .wj-track { display: flex; height: 100%; will-change: transform; }

        .wj-panel {
          flex: 0 0 100vw; height: 100%;
          display: flex; align-items: center; justify-content: center;
          gap: 4vw; padding: 0 7vw;
        }
        @media (max-width: 768px) {
          .wj-panel { flex-direction: column; justify-content: center; gap: 2.5rem; padding: 12vh 8vw 0; }
        }

        .wj-panel-text { flex: 1 1 48%; max-width: 640px; }
        .wj-panel-visual { flex: 1 1 50%; display: flex; align-items: center; justify-content: center; }
        ${VISUAL_CSS}

        .wj-kicker {
          font-size: 13px; letter-spacing: 0.24em; text-transform: uppercase;
          font-weight: 600; color: var(--color-accent);
        }
        .wj-headline {
          margin-top: 1.3rem;
          font-family: var(--font-display-serif, Georgia, serif);
          font-size: clamp(2.4rem, 4.6vw, 4.6rem); line-height: 1.04; color: var(--color-ink);
          font-variation-settings: "opsz" 72, "wght" 600;
        }
        .wj-body {
          margin-top: 1.6rem; max-width: 50ch;
          font-size: clamp(1.1rem, 1.5vw, 1.45rem); line-height: 1.6; color: var(--color-ink-muted);
          white-space: pre-line;
        }

        .wj-eyebrow {
          /* Clear the 96px fixed navbar (z-30) — at top:6vh this label sat
             behind the bar and read as invisible. */
          position: absolute; top: calc(96px + 3vh); inset-inline-start: 9vw; z-index: 10;
          font-size: 14px; letter-spacing: 0.22em; text-transform: uppercase;
          font-weight: 700; color: var(--color-accent);
        }

        .wj-progress {
          position: absolute; bottom: 5vh; inset-inline: 0; z-index: 10;
          margin-inline: auto; width: min(440px, 64vw); height: 4px;
          border-radius: 999px; overflow: hidden;
          background: color-mix(in oklab, var(--color-blue) 18%, transparent);
        }
        .wj-progress-fill {
          height: 100%; width: 100%; border-radius: 999px;
          background: var(--color-accent);
          transform-origin: left center;
        }
        html[dir="rtl"] .wj-progress-fill { transform-origin: right center; }
      `}</style>
    </section>
  )
}
